import React, { useState, useEffect, useMemo } from "react";
import {
  Alert,
  Grid,
  Box,
  Typography,
  Button,
  Container,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ApiSelect from "../../../form/ApiSelect";
import PublishToggle from "../../../form/PublishToggle";
import PrefectureSelect from "../../../../shared/PrefectureSelect";
import DateRangeInput from "../../../form/DateRangeInput";
import MinMaxInput from "../../../form/MinMaxInput";
import FormTextField from "../../../form/FormTextField";
import FormMultilineField from "../../../form/FormMultilineField";
import SkillExperience from "../../../form/SkillExperience";
import OperationExperience from "../../../form/OperationExperience";
import RoleExperience from "../../../form/RoleExperience";
import FormSelect from "../../../form/FormSelect";
import ToggleButtonRadio from "../../../form/ToggleButtonRadio";
import { formatJpDateTime } from "../../../../utils/dateUtils";
import { buildRequestBody } from "../../../utils/buildRequestBody";
import {
  getDefaultProjectFormValues,
  defaultSkill,
  defaultRole,
  initialOperationExperience,
} from "../../../config/defaultValues";

const ProjectEditForm = ({ currentUser, csrfToken }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  // project取得用state
  const [project, setProject] = useState(null);
  const [formState, setFormState] = useState(null);
  const [operationExperience, setOperationExperience] = useState(
    initialOperationExperience
  );
  const [errors, setErrors] = useState([]);
  const [validationTriggered, setValidationTriggered] = useState(false);

  // work_style の enum値と日本語ラベルを同期
  const WORK_STYLES = [
    { value: "on_site", label: "常駐" },
    { value: "remote", label: "フルリモート" },
    { value: "hybrid", label: "ハイブリッド" },
  ];

  // commercial_flow の enum値と日本語ラベルを同期
  const COMMERCIAL_FLOW = [
    { value: "end_client_company", label: "エンド直" },
    { value: "primary_contractor_company", label: "元受け直" },
    { value: "other", label: "その他" },
  ];

  // APIからプロジェクト取得
  useEffect(() => {
    fetch(`/company/projects/${id}.json`)
      .then((res) => res.json())
      .then((data) => {
        setProject(data);
      })
      .catch(() => setErrors(["プロジェクト情報の取得に失敗しました"]));
  }, [id]);

  // プロジェクト・ユーザー取得後、formState初期化
  useEffect(() => {
    if (project && currentUser) {
      setFormState({
        ...getDefaultProjectFormValues(currentUser.id),
        ...project,
        skills: (project.skills ?? []).filter(Boolean).map((s) => ({
          id: s.id,
          skill_id: s.skill_id,
          skill_name: s.skill_name ?? "",
          experience_year: s.experience_year ?? "",
          level: s.level ?? "",
          visible_flag: s.visible_flag ?? true,
        })),
        roles: (project.roles ?? []).filter(Boolean).map((r) => ({
          id: r.id,
          role_id: r.role_id ?? null,
          experience_year: r.experience_year ?? "",
        })),
        operations: (project.operations ?? []).filter(Boolean).map((p) => ({
          id: p.id,
          operation_id: p.operation_id ?? null,
          experience_year: p.experience_year ?? "",
        })),
      });
      setOperationExperience(
        (project.operations ?? []).filter(Boolean).map((p) => ({
          id: p.id,
          operation_id: p.operation_id ?? null,
          experience_year: p.experience_year ?? "",
        }))
      );
    }
  }, [project, currentUser]);

  // operationExperience変更時もformState反映
  useEffect(() => {
    if (formState) {
      const mapped = operationExperience
        .filter((op) => op.operation_id)
        .map((op) => ({
          operation_id: op.operation_id,
          experience_year: op.experience_year || "",
        }));
      setFormState((prev) => (prev ? { ...prev, operations: mapped } : prev));
    }
  }, [operationExperience]);

  // 重複スキルを一意化
  const dedupedSkills = useMemo(() => {
    if (!formState || !formState.skills) return [];
    const seen = new Set();
    return formState.skills.filter((s) => {
      const key = s.skill_id
        ? `id:${s.skill_id}`
        : s.skill_name?.trim()
        ? `name:${s.skill_name.trim()}`
        : "";
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [formState]);

  // エラーが出たらページ上部にスクロール
  useEffect(() => {
    if (validationTriggered && errors.length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [validationTriggered, errors]);

  // 指定された日付が過去かを判定（空文字は除外）
  const isDateInPast = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateStr) < today;
  };

  // 終了日が開始日以前であればエラー（同日はOK）
  const isEndDateInvalid = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return false;
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    return end < start;
  };

  // 数値の最小値が最大値以上かどうかをチェック（同じは許容）
  const isMinGreaterThanMax = (minVal, maxVal) => {
    if (minVal === "" || maxVal === "") return false;
    const min = Number(minVal);
    const max = Number(maxVal);
    return min > max;
  };

  // 空文字・null・undefined を空とみなす
  const isEmpty = (value) =>
    value === "" || value === null || value === undefined;

  // 数値が空または負の値か
  const isEmptyOrNegative = (value) => {
    const num = Number(value);
    return value === "" || isNaN(num) || num < 0;
  };

  // 日付
  const isInvalidPublishStartDate =
    validationTriggered &&
    (isEmpty(formState.publish_start_date) ||
      isDateInPast(formState.publish_start_date));

  const isInvalidPublishEndDate =
    validationTriggered &&
    (isEmpty(formState.publish_end_date) ||
      isDateInPast(formState.publish_end_date) ||
      isEndDateInvalid(
        formState.publish_start_date,
        formState.publish_end_date
      ));

  const isInvalidWorkStartDate =
    validationTriggered &&
    (isEmpty(formState.work_start_date) ||
      isDateInPast(formState.work_start_date));

  const isInvalidWorkEndDate =
    validationTriggered &&
    !formState.long_term_flag && // 長期の場合はスキップ
    (isEmpty(formState.work_end_date) ||
      isDateInPast(formState.work_end_date) ||
      isEndDateInvalid(formState.work_start_date, formState.work_end_date));

  // 必須入力
  const isInvalidProjectManager =
    validationTriggered && isEmpty(formState.project_manager_id);
  const isInvalidProjectName = validationTriggered && isEmpty(formState.name);
  const isInvalidProjectDescription =
    validationTriggered && isEmpty(formState.description);
  const isInvalidProjectTitle = validationTriggered && isEmpty(formState.title);
  const isInvalidInterviewCount =
    validationTriggered && isEmpty(formState.interview_count);
  const isInvalidWorkStyle =
    validationTriggered && isEmpty(formState.work_style);

  // 稼働形態がハイブリッドor常駐のときのみ勤務地は必須
  const isInvalidPrefecture =
    validationTriggered &&
    shouldShowPrefecture &&
    formState.prefecture_ids.length === 0;

  // スキルのチェック
  const isInvalidSkill =
    validationTriggered &&
    (() => {
      const validSkills = dedupedSkills.filter((skill) =>
        skill.skill_name?.trim()
      );

      // 有効なスキルが1つもない場合
      if (validSkills.length === 0) return true;

      // 重複チェック
      const names = validSkills.map((s) => s.skill_name.trim());
      return new Set(names).size !== names.length;
    })();

  // 金額、時間
  const isInvalidPriceMin =
    validationTriggered &&
    (isEmpty(formState.price_min) || isEmptyOrNegative(formState.price_min));

  const isInvalidPriceMax =
    validationTriggered &&
    (isEmpty(formState.price_max) ||
      isEmptyOrNegative(formState.price_max) ||
      isMinGreaterThanMax(formState.price_min, formState.price_max));

  const isInvalidWorkloadMin =
    validationTriggered &&
    (isEmpty(formState.workload_min) ||
      isEmptyOrNegative(formState.workload_min));

  const isInvalidWorkloadMax =
    validationTriggered &&
    (isEmpty(formState.workload_max) ||
      isEmptyOrNegative(formState.workload_max) ||
      isMinGreaterThanMax(formState.workload_min, formState.workload_max));

  // otherの場合、商流詳細は必須
  const isCommercialFlowDetail =
    validationTriggered &&
    formState.commercial_flow === "other" &&
    isEmpty(formState.commercial_flow_detail);

  // バリデーション
  const validateForm = () => {
    const newErrors = [];

    const rules = [
      {
        field: "project_manager_id",
        isError: isEmpty(formState.project_manager_id),
        message: "担当者は入力必須です。",
      },
      {
        field: "publish_start_date",
        isError: isEmpty(formState.publish_start_date),
        message: "掲載期間の開始日は入力必須です。",
      },
      {
        field: "publish_end_date",
        isError: isEmpty(formState.publish_end_date),
        message: "掲載期間の終了日は入力必須です。",
      },
      {
        field: "publish_start_date",
        isError:
          !isEmpty(formState.publish_start_date) &&
          isDateInPast(formState.publish_start_date),
        message: "掲載期間の開始日は今日以降の日付を指定してください。",
      },
      {
        field: "publish_end_date",
        isError:
          !isEmpty(formState.publish_end_date) &&
          isDateInPast(formState.publish_end_date),
        message: "掲載期間の終了日は今日以降の日付を指定してください。",
      },
      {
        field: "publish_end_date",
        isError:
          !isEmpty(formState.publish_start_date) &&
          !isEmpty(formState.publish_end_date) &&
          isEndDateInvalid(
            formState.publish_start_date,
            formState.publish_end_date
          ),
        message: "掲載期間の終了日は掲載期間の開始日以降に設定してください。",
      },
      {
        field: "name",
        isError: isEmpty(formState.name),
        message: "プロジェクト名は入力必須です。",
      },
      {
        field: "description",
        isError: isEmpty(formState.description),
        message: "プロジェクト概要は入力必須です。",
      },
      {
        field: "title",
        isError: isEmpty(formState.title),
        message: "提案用タイトルは入力必須です。",
      },
      {
        field: "work_start_date",
        isError: isEmpty(formState.work_start_date),
        message: "稼働期間の開始日は入力必須です。",
      },
      {
        field: "work_end_date_or_long_term",
        isError: !formState.work_end_date && !formState.long_term_flag,
        message: "稼働終了日または長期のどちらかを入力してください。",
      },
      {
        field: "work_start_date",
        isError:
          !isEmpty(formState.work_start_date) &&
          isDateInPast(formState.work_start_date),
        message: "稼働期間の開始日は今日以降の日付を指定してください。",
      },
      {
        field: "work_end_date",
        isError:
          !isEmpty(formState.work_end_date) &&
          isDateInPast(formState.work_end_date),
        message: "稼働期間の終了日は今日以降の日付を指定してください。",
      },
      {
        field: "work_end_date",
        isError:
          !isEmpty(formState.work_start_date) &&
          !isEmpty(formState.work_end_date) &&
          isEndDateInvalid(formState.work_start_date, formState.work_end_date),
        message: "稼働期間の終了日は稼働期間の開始日以降に設定してください。",
      },
      {
        field: "price_min",
        isError: isEmptyOrNegative(formState.price_min),
        message: "金額の下限は0以上の数値を入力してください。",
      },
      {
        field: "price_max",
        isError: isEmptyOrNegative(formState.price_max),
        message: "金額の上限は0以上の数値を入力してください。",
      },
      {
        field: "price_max",
        isError:
          !isEmpty(formState.price_min) &&
          !isEmpty(formState.price_max) &&
          isMinGreaterThanMax(formState.price_min, formState.price_max),
        message: "金額の上限は下限以上にしてください。",
      },
      {
        field: "workload_min",
        isError: isEmptyOrNegative(formState.workload_min),
        message: "稼働時間の下限は0以上の数値を入力してください。",
      },
      {
        field: "workload_max",
        isError: isEmptyOrNegative(formState.workload_max),
        message: "稼働時間の上限は0以上の数値を入力してください。",
      },
      {
        field: "workload_max",
        isError:
          !isEmpty(formState.workload_min) &&
          !isEmpty(formState.workload_max) &&
          isMinGreaterThanMax(formState.workload_min, formState.workload_max),
        message: "稼働時間の上限は下限以上にしてください。",
      },
      {
        field: "prefecture_ids",
        isError: shouldShowPrefecture && formState.prefecture_ids.length === 0,
        message: "勤務地エリアは最低1つ選択してください。",
      },
      {
        field: "commercial_flow_detail",
        isError:
          formState.commercial_flow === "other" &&
          isEmpty(formState.commercial_flow_detail),
        message: "商流が「その他」の場合、商流詳細は入力必須です。",
      },
      {
        field: "skills",
        isError: (() => {
          const filledSkills = dedupedSkills.filter((skill) =>
            skill.skill_name?.trim()
          );
          return filledSkills.length === 0;
        })(),
        message: "スキルは1つ以上入力してください。",
      },
      {
        field: "skills",
        isError: (() => {
          const names = dedupedSkills
            .map((s) => s.skill_name?.trim())
            .filter(Boolean);
          const unique = new Set(names);
          return names.length !== unique.size;
        })(),
        message: "同じスキル名を複数入力することはできません。",
      },
      {
        field: "interview_count",
        isError: isEmpty(formState.interview_count),
        message: "面談回数は入力必須です。",
      },
    ];

    rules.forEach((rule) => {
      if (rule.isError) {
        newErrors.push(rule.message);
      }
    });

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  // スキルの重複チェック
  const duplicateSkillNames = useMemo(() => {
    if (!formState || !formState.skills) return [];
    const names = formState.skills
      .map((s) => s.skill_name?.trim())
      .filter(Boolean);
    return names.filter((name, i, arr) => arr.indexOf(name) !== i);
  }, [formState]);

  // useNavigate を使ったSPA送信
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setValidationTriggered(true);
      return;
    }

    if (!project || !project.id) {
      setErrors(["プロジェクト情報が正しくありません。"]);
      return;
    }

    // 送信用のbodyをAPI用JSON形式に変換
    const body = buildRequestBody(formState, project);

    try {
      const res = await fetch(`/company/projects/${project.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        // SPA遷移で詳細へ
        navigate(`/company/projects/${project.id}`, {
          state: { flashMessage: "プロジェクトを更新しました" },
        });
      } else {
        const data = await res.json();
        setErrors(data.errors || ["保存に失敗しました。"]);
        setValidationTriggered(true);
      }
    } catch (error) {
      setErrors(["通信エラーが発生しました"]);
      setValidationTriggered(true);
    }
  };

  // フォーム値のonChange
  const handleChange = (e) => {
    setValidationTriggered(false);
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  // 稼働形態がハイブリッドor常駐のときだけエリアを表示
  const shouldShowPrefecture =
    formState?.work_style === "hybrid" || formState?.work_style === "on_site";

  // プロジェクトがまだ来ていない間はローディング
  if (!project || !formState || !currentUser) {
    return (
      <Box p={4}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  // レンダリング
  return (
    <Container maxWidth="md" sx={{ position: "relative", mt: 2, mb: 10 }}>
      <Button
        onClick={() => window.history.back()}
        size="small"
        sx={{
          mb: 0.5,
          color: "#000",
          fontWeight: "bold",
          textTransform: "none",
          "&:hover": { opacity: 0.7 },
        }}
      >
        ← 戻る
      </Button>

      <form id="project-form" onSubmit={handleSubmit}>
        <Box sx={{ bgcolor: "#fff", p: 4, borderRadius: 2, boxShadow: 1 }}>
          {/* エラーの表示 */}
          {validationTriggered && errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.map((error, idx) => (
                <div key={idx}>{error}</div>
              ))}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              プロジェクト編集
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                mt: 1,
                display: "block",
                borderBottom: "1px solid #ccc",
                pb: 1,
              }}
            >
              更新日時：{formatJpDateTime(project.updated_at)}
            </Typography>
          </Box>

          <Grid container spacing={1}>
            {/* 担当者（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <ApiSelect
                value={formState.project_manager_id ?? ""}
                onChange={(val) =>
                  setFormState({ ...formState, project_manager_id: val })
                }
                isRequired={true}
                endpoint="/company/users.json"
                label="プロジェクトの担当者"
                currentUserId={currentUser.id}
                error={isInvalidProjectManager}
                blankLabel="担当者を選択"
              />
            </Grid>

            {/* 掲載期間（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <DateRangeInput
                label="掲載期間"
                nameStart="publish_start_date"
                nameEnd="publish_end_date"
                valueStart={formState.publish_start_date ?? ""}
                valueEnd={formState.publish_end_date ?? ""}
                onChange={handleChange}
                isRequired={true}
                errorStart={isInvalidPublishStartDate}
                errorEnd={isInvalidPublishEndDate}
              />
            </Grid>

            {/* プロジェクト名（共通コンポーネント） */}
            <Grid item xs={12}>
              <FormTextField
                label="プロジェクト名（最大50文字まで）"
                name="name"
                value={formState.name ?? ""}
                onChange={handleChange}
                placeholder="業務プロセス改善支援システム"
                isRequired={true}
                maxLength={50}
                error={isInvalidProjectName}
              />
            </Grid>

            {/* プロジェクト概要（共通コンポーネント） */}
            <Grid item xs={12}>
              <FormMultilineField
                label="プロジェクト概要（最大1000文字まで）"
                rows={10}
                name="description"
                value={formState.description ?? ""}
                onChange={handleChange}
                placeholder="クライアントの業務DXを支援するため、Vue.js + Laravel を用いた社内管理システムを新規開発。要件定義～リリースまでを5名チームで実施。"
                isRequired={true}
                maxLength={1000}
                error={isInvalidProjectDescription}
              />
            </Grid>

            {/* 提案用タイトル（共通コンポーネント） */}
            <Grid item xs={12}>
              <FormTextField
                label="提案用タイトル（最大70文字まで）"
                name="title"
                value={formState.title ?? ""}
                onChange={handleChange}
                placeholder="【要件定義〜開発】Vue.js×Laravel｜業務効率化Webシステム開発／フルリモートOK・年収応相談"
                isRequired={true}
                maxLength={70}
                error={isInvalidProjectTitle}
              />
            </Grid>

            {/* 稼働期間（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <DateRangeInput
                label="稼働期間"
                nameStart="work_start_date"
                nameEnd="work_end_date"
                valueStart={formState.work_start_date ?? ""}
                valueEnd={formState.work_end_date ?? ""}
                onChange={handleChange}
                isRequired={true}
                errorStart={isInvalidWorkStartDate}
                errorEnd={isInvalidWorkEndDate}
                endDisabled={!!formState.long_term_flag}
                endFieldAdd={
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formState.long_term_flag}
                        onChange={(e) => {
                          setFormState((prev) => ({
                            ...prev,
                            long_term_flag: e.target.checked,
                            work_end_date: e.target.checked
                              ? ""
                              : prev.work_end_date,
                          }));
                        }}
                      />
                    }
                    label="長期"
                    sx={{ mt: 1 }}
                  />
                }
              />
            </Grid>

            {/* 金額（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <MinMaxInput
                label="金額"
                nameMin="price_min"
                nameMax="price_max"
                valueMin={formState.price_min ?? ""}
                valueMax={formState.price_max ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 6) handleChange(e);
                }}
                placeholderMin="30"
                placeholderMax="50"
                unit="万円"
                isRequired={true}
                errorMin={isInvalidPriceMin}
                errorMax={isInvalidPriceMax}
              />
            </Grid>

            {/* 稼働時間（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <MinMaxInput
                label="稼働時間"
                nameMin="workload_min"
                nameMax="workload_max"
                valueMin={formState.workload_min ?? ""}
                valueMax={formState.workload_max ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 6) handleChange(e);
                }}
                placeholderMin="150"
                placeholderMax="180"
                unit="時間"
                isRequired={true}
                errorMin={isInvalidWorkloadMin}
                errorMax={isInvalidWorkloadMax}
              />
            </Grid>

            {/* 稼働形態（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <FormSelect
                label="稼働形態"
                options={WORK_STYLES}
                value={formState.work_style}
                onChange={(val) =>
                  setFormState({ ...formState, work_style: val })
                }
                isRequired={true}
                error={isInvalidWorkStyle}
                placeholder="稼働形態を選択"
                fullWidth
              />
            </Grid>

            {shouldShowPrefecture && (
              <>
                {/* 都道府県（共通コンポーネント）：稼働形態がハイブリッドor常駐のときだけエリアを表示 */}
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <PrefectureSelect
                    value={formState.prefecture_ids ?? []}
                    onChange={(ids) =>
                      setFormState((prev) => ({ ...prev, prefecture_ids: ids }))
                    }
                    label="勤務地エリア"
                    max={3}
                    isRequired={true}
                    error={isInvalidPrefecture}
                  />
                </Grid>
              </>
            )}

            {/* 商流（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <ToggleButtonRadio
                label="商流"
                name="commercial_flow"
                value={formState.commercial_flow}
                onChange={handleChange}
                options={COMMERCIAL_FLOW}
                isRequired={true}
              />
            </Grid>

            {/* 商流詳細（共通コンポーネント） */}
            <Grid item xs={12}>
              <FormMultilineField
                label="商流詳細（最大200文字まで）"
                rows={5}
                name="commercial_flow_detail"
                value={formState.commercial_flow_detail}
                onChange={handleChange}
                placeholder="商流についての詳細があれば記載、その他の場合は必須"
                isRequired={formState.commercial_flow === "other"}
                error={isCommercialFlowDetail}
                maxLength={200}
              />
            </Grid>

            {/* スキル（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <SkillExperience
                skills={formState.skills}
                onChange={(updated) =>
                  setFormState({ ...formState, skills: updated })
                }
                onAdd={() =>
                  setFormState({
                    ...formState,
                    skills: [...formState.skills, { ...defaultSkill }],
                  })
                }
                onRemove={(index) =>
                  setFormState({
                    ...formState,
                    skills: formState.skills.filter((_, i) => i !== index),
                  })
                }
                isRequired={true}
                error={isInvalidSkill}
                duplicateSkillNames={duplicateSkillNames}
              />
            </Grid>

            <Grid item xs={12}>
              <FormMultilineField
                label="スキル詳細（最大500文字まで）"
                rows={5}
                name="skill_detail"
                value={formState.skill_detail}
                onChange={handleChange}
                placeholder="スキルについての詳細があれば記載"
                maxLength={500}
              />
            </Grid>

            {/* 工程（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <OperationExperience
                value={operationExperience}
                onChange={setOperationExperience}
                isRequired={false}
              />
            </Grid>

            <Grid item xs={12}>
              <FormMultilineField
                label="工程詳細（最大500文字まで）"
                rows={5}
                name="operation_detail"
                value={formState.operation_detail}
                onChange={handleChange}
                placeholder="工程についての詳細があれば記載"
                maxLength={500}
              />
            </Grid>

            {/* 役割（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <RoleExperience
                roles={formState.roles}
                onChange={(updated) =>
                  setFormState({ ...formState, roles: updated })
                }
                onAdd={() =>
                  setFormState({
                    ...formState,
                    roles: [...formState.roles, { ...defaultRole }],
                  })
                }
                onRemove={(index) =>
                  setFormState({
                    ...formState,
                    roles: formState.roles.filter((_, i) => i !== index),
                  })
                }
                isRequired={false}
              />
            </Grid>

            <Grid item xs={12}>
              <FormMultilineField
                label="ポジション詳細（最大500文字まで）"
                rows={5}
                name="role_detail"
                value={formState.role_detail}
                onChange={handleChange}
                placeholder="ポジションについての詳細があれば記載"
                maxLength={500}
              />
            </Grid>

            {/* 面談回数（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <FormTextField
                label="面談回数"
                name="interview_count"
                value={formState.interview_count ?? ""}
                onChange={handleChange}
                placeholder="1~2回"
                isRequired={true}
                error={isInvalidInterviewCount}
              />
            </Grid>

            {/* 備考（共通コンポーネント） */}
            <Grid item xs={12}>
              <FormMultilineField
                label="備考（最大1000文字まで）"
                rows={10}
                name="remark"
                value={formState.remark ?? ""}
                onChange={handleChange}
                placeholder="補足事項や特記事項をご記入ください"
                isRequired={false}
                maxLength={1000}
              />
            </Grid>

            {/* 公開フラグ（共通コンポーネント） */}
            <Grid item xs={12}>
              <PublishToggle
                label="公開設定"
                value={formState.published_flag}
                onChange={(val) =>
                  setFormState({ ...formState, published_flag: val })
                }
                publishEnd={formState.publish_end_date}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              position: "absolute",
              bottom: -60,
              right: 16,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate(`/company/projects/${project.id}`)}
              sx={{
                backgroundColor: "#ffffff",
                color: "#000",
                borderRadius: 9999,
                px: 4,
                fontWeight: "bold",
                border: "1px solid #9ca3af",
                "&:hover": {
                  opacity: 0.9,
                  backgroundColor: "#f3f4f6",
                  borderColor: "#9ca3af",
                },
                textAlign: "center",
              }}
            >
              キャンセル
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="inherit"
              sx={{
                borderRadius: 4,
                color: "#fff",
                bgcolor: "#000",
                "&:hover": { bgcolor: "#333" },
              }}
            >
              更新
            </Button>
          </Box>
        </Box>
      </form>
    </Container>
  );
};

export default ProjectEditForm;
